import { ReactNode, useRef } from "react";
import { DragDropContext, Droppable, DropResult } from "@hello-pangea/dnd";
import { QueryKey, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils/utils";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Close } from "@dreamhub/designsystem/icons";
import ToastBody from "@/components/ui/toast-body";
import { BaseStageRow } from "./types";

export interface ReorderRequest<T extends BaseStageRow> {
  moved: T;
  newItems: T[];
  sourceIndex: number;
  destinationIndex: number;
}

interface Props<T extends BaseStageRow> {
  items: T[];
  onItemsChange: (items: T[]) => void;
  renderRow: (params: {
    item: T;
    index: number;
    isDragDisabled: boolean;
    isLast: boolean;
  }) => ReactNode;
  tPrefix: string;
  queryKey: QueryKey;
  persistOrder: (orderedIds: Array<T["id"]>) => Promise<unknown>;
  // Returns a function to start a confirmation flow (it must call `commit` on approval).
  // Return null/undefined to commit the new order immediately.
  shouldConfirmReorder?: (
    request: ReorderRequest<T>
  ) => ((commit: () => void) => void) | null | undefined;
  droppableId?: string;
  className?: string;
}

const DraggableStagesList = <T extends BaseStageRow>({
  items,
  onItemsChange,
  renderRow,
  tPrefix,
  queryKey,
  persistOrder,
  shouldConfirmReorder,
  droppableId = "stages-list",
  className = ""
}: Props<T>) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const originalItemsRef = useRef<T[]>([]);

  const commitOrder = async (newStageItems: T[]) => {
    originalItemsRef.current = [...items];
    const orderedIds = newStageItems.map((s) => s.id);
    try {
      await persistOrder(orderedIds);
      queryClient.invalidateQueries({ queryKey });
      toast.success(
        <ToastBody
          title={t(`${tPrefix}.successToast`)}
          actions={
            <>
              <Button
                variant="secondaryNoBg"
                onClick={async () => {
                  if (originalItemsRef.current.length === 0) return;
                  try {
                    await persistOrder(
                      originalItemsRef.current.map((s) => s.id)
                    );
                    queryClient.invalidateQueries({ queryKey });
                    onItemsChange(originalItemsRef.current);
                    originalItemsRef.current = [];
                  } catch (error) {
                    console.error("Failed to undo stage order:", error);
                  }
                  toast.dismiss();
                }}
              >
                {t("undo")}
              </Button>
              <IconButton
                size="md"
                variant="iconNoBg"
                iconColor="secondary"
                onClick={() => toast.dismiss()}
              >
                <Close />
              </IconButton>
            </>
          }
        />,
        { duration: 5000 }
      );
    } catch (error) {
      console.error("Failed to update stage order:", error);
      onItemsChange(items);
    }
  };

  const handleDragEnd = (result: DropResult) => {
    const { destination, source } = result;
    if (!destination || destination.index === source.index) return;

    const newItems = [...items];
    const [moved] = newItems.splice(source.index, 1);
    newItems.splice(destination.index, 0, moved);

    const startConfirmation = shouldConfirmReorder?.({
      moved,
      newItems,
      sourceIndex: source.index,
      destinationIndex: destination.index
    });

    if (startConfirmation) {
      startConfirmation(() => {
        onItemsChange(newItems);
        commitOrder(newItems);
      });
      return;
    }

    onItemsChange(newItems);
    commitOrder(newItems);
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId={droppableId} isDropDisabled={items.length <= 1}>
        {(provided) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={cn(
              "rounded-2xl border border-border-border1 bg-surface-widget-card overflow-hidden",
              className
            )}
            data-test="stages-list"
          >
            {items.map((item, index) =>
              renderRow({
                item,
                index,
                isDragDisabled: items.length <= 1,
                isLast: index === items.length - 1
              })
            )}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default DraggableStagesList;
